import { useState, useEffect, useRef, useCallback } from "react";
import { chatApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useChatUnread } from "@/context/ChatUnreadContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/SimpleToast";
import { VoiceInputButton } from "@/components/VoiceInputButton";
import {
  MessageSquare,
  Send,
  Loader2,
  Trash2,
  Pencil,
  Check,
  X,
  RefreshCw
} from "lucide-react";

export default function TeamChat() {
  const toast = useToast();
  const { user: currentUser } = useAuth();
  const { markRead } = useChatUnread();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [newMessage, setNewMessage] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const lastMarkedRef = useRef(null);

  const fetchMessages = useCallback(async () => {
    try {
      const res = await chatApi.getMessages(100);
      const msgs = Array.isArray(res.data) ? res.data : [];
      setMessages(msgs);
      // Only mark as read when a genuinely newer message exists (avoids a write on every poll)
      const newest = msgs.reduce((m, x) => (x.created_at > m ? x.created_at : m), "");
      if (newest !== lastMarkedRef.current) {
        lastMarkedRef.current = newest;
        markRead();
      }
    } catch (error) {
      console.warn("Chat konnte nicht geladen werden", error);
    } finally {
      setLoading(false);
    }
  }, [markRead]);

  useEffect(() => {
    fetchMessages();
    // Poll for new messages every 5 seconds
    const interval = setInterval(fetchMessages, 5000);
    return () => clearInterval(interval);
  }, [fetchMessages]);

  useEffect(() => {
    // Scroll to bottom when messages change
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    setSending(true);
    try {
      await chatApi.sendMessage(newMessage.trim());
      setNewMessage("");
      fetchMessages();
      inputRef.current?.focus();
    } catch (error) {
      toast.error("Nachricht konnte nicht gesendet werden");
    } finally {
      setSending(false);
    }
  };

  const handleDeleteMessage = async (messageId) => {
    try {
      await chatApi.deleteMessage(messageId);
      setMessages(messages.filter(m => m.id !== messageId));
      toast.success("Nachricht gelöscht");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Löschen fehlgeschlagen");
    }
  };

  const handleStartEdit = (message) => {
    setEditingId(message.id);
    setEditText(message.message);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditText("");
  };

  const handleSaveEdit = async (messageId) => {
    if (!editText.trim()) return;
    setSavingEdit(true);
    try {
      const res = await chatApi.updateMessage(messageId, editText.trim());
      setMessages(messages.map(m => (m.id === messageId ? res.data : m)));
      setEditingId(null);
      setEditText("");
      toast.success("Nachricht bearbeitet");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Bearbeiten fehlgeschlagen");
    } finally {
      setSavingEdit(false);
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const today = new Date();
    const isToday = date.toDateString() === today.toDateString();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const isYesterday = date.toDateString() === yesterday.toDateString();

    const time = date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
    
    if (isToday) return time;
    if (isYesterday) return `Gestern ${time}`;
    return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }) + ` ${time}`;
  };

  const getInitials = (name) => {
    if (!name) return "?";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  const getAvatarColor = (userId) => {
    const colors = [
      "bg-blue-500", "bg-emerald-500", "bg-purple-500", 
      "bg-amber-500", "bg-rose-500", "bg-cyan-500"
    ];
    const index = userId ? userId.charCodeAt(0) % colors.length : 0;
    return colors[index];
  };

  // Group messages by date
  const groupedMessages = messages.reduce((groups, message) => {
    const date = new Date(message.created_at).toDateString();
    if (!groups[date]) groups[date] = [];
    groups[date].push(message);
    return groups;
  }, {});

  const formatDateHeader = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) return "Heute";
    if (date.toDateString() === yesterday.toDateString()) return "Gestern";
    return date.toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: 'long' });
  };

  return (
    <div className="h-[calc(100dvh-12rem)] lg:h-[calc(100vh-8rem)] flex flex-col animate-fade-in" data-testid="chat-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Team-Chat</h1>
          <p className="text-muted-foreground mt-1">Interner Kommunikationskanal</p>
        </div>
        <Button variant="outline" size="icon" onClick={fetchMessages} title="Aktualisieren">
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      <Card className="flex-1 flex flex-col overflow-hidden">
        <CardHeader className="py-3 border-b flex-shrink-0">
          <CardTitle className="text-lg flex items-center gap-2">
            <MessageSquare className="w-5 h-5" />
            Allgemeiner Chat
            <span className="text-sm font-normal text-muted-foreground ml-2">
              {messages.length} Nachrichten
            </span>
          </CardTitle>
        </CardHeader>
        
        <CardContent className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="chat-messages">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="spinner" />
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
              <MessageSquare className="w-16 h-16 opacity-30 mb-4" />
              <p>Noch keine Nachrichten</p>
              <p className="text-sm">Starten Sie die Konversation!</p>
            </div>
          ) : (
            Object.entries(groupedMessages).map(([date, msgs]) => (
              <div key={date}>
                <div className="flex items-center gap-4 my-4">
                  <div className="flex-1 h-px bg-border" />
                  <span className="text-xs text-muted-foreground font-medium">{formatDateHeader(date)}</span>
                  <div className="flex-1 h-px bg-border" />
                </div>
                <div className="space-y-3">
                  {msgs.map((message) => {
                    const isOwnMessage = currentUser?.id && message.user_id === currentUser.id;
                    const isEditing = editingId === message.id;
                    return (
                      <div
                        key={message.id}
                        className={`flex gap-3 group ${isOwnMessage ? 'flex-row-reverse' : ''}`}
                        data-testid={`chat-message-${message.id}`}
                      >
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-medium flex-shrink-0 ${getAvatarColor(message.user_id)}`}>
                          {getInitials(message.user_name)}
                        </div>
                        <div className={`flex flex-col max-w-[78%] sm:max-w-[70%] ${isOwnMessage ? 'items-end' : 'items-start'}`}>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-medium">{message.user_name}</span>
                            <span className="text-xs text-muted-foreground">{formatTime(message.created_at)}</span>
                            {message.edited_at && (
                              <span className="text-xs text-muted-foreground italic">(bearbeitet)</span>
                            )}
                          </div>
                          {isEditing ? (
                            <div className="flex items-center gap-2 w-full">
                              <Input
                                value={editText}
                                onChange={(e) => setEditText(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') { e.preventDefault(); handleSaveEdit(message.id); }
                                  if (e.key === 'Escape') handleCancelEdit();
                                }}
                                autoFocus
                                className="flex-1 min-w-0"
                                data-testid={`chat-edit-input-${message.id}`}
                              />
                              <Button size="icon" className="h-9 w-9 shrink-0" disabled={savingEdit || !editText.trim()} onClick={() => handleSaveEdit(message.id)} data-testid={`chat-edit-save-${message.id}`}>
                                {savingEdit ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                              </Button>
                              <Button size="icon" variant="outline" className="h-9 w-9 shrink-0" onClick={handleCancelEdit} data-testid={`chat-edit-cancel-${message.id}`}>
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          ) : (
                            <div className={`rounded-2xl px-4 py-2 ${
                              isOwnMessage
                                ? 'bg-primary text-primary-foreground rounded-tr-sm'
                                : 'bg-muted rounded-tl-sm'
                            }`}>
                              <p className="text-sm whitespace-pre-wrap break-words">{message.message}</p>
                            </div>
                          )}
                          {isOwnMessage && !isEditing && (
                            <div className="flex gap-1 mt-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                              <button
                                type="button"
                                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary px-1.5 py-1 rounded transition-colors"
                                onClick={() => handleStartEdit(message)}
                                data-testid={`chat-edit-btn-${message.id}`}
                              >
                                <Pencil className="w-3 h-3" />
                                Bearbeiten
                              </button>
                              <button
                                type="button"
                                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-destructive px-1.5 py-1 rounded transition-colors"
                                onClick={() => handleDeleteMessage(message.id)}
                                data-testid={`chat-delete-btn-${message.id}`}
                              >
                                <Trash2 className="w-3 h-3" />
                                Löschen
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </CardContent>

        <div className="p-4 border-t flex-shrink-0">
          <form onSubmit={handleSendMessage} className="flex gap-2">
            <Input
              ref={inputRef}
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Nachricht schreiben..."
              disabled={sending}
              className="flex-1"
              data-testid="chat-input"
            />
            <VoiceInputButton
              variant="outline"
              size="icon"
              onTranscript={(t) => setNewMessage((prev) => (prev ? `${prev} ${t}` : t))}
            />
            <Button type="submit" disabled={sending || !newMessage.trim()} data-testid="chat-send-btn">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </form>
        </div>
      </Card>
    </div>
  );
}
